/**
 * Spherical geodesy for VFR route work: great-circle distance, initial true
 * course, dead-reckoned destination point and leg midpoint.
 *
 * The model is a sphere of the IUGG mean Earth radius R1 = (2a+b)/3 =
 * 6371.0088 km. Against a WGS-84 geodesic that is worth at most ~0.5 % of the
 * leg length (a few tenths of a NM on a 100 NM leg) — well inside the whole-NM
 * resolution of a paper navlog, and the four functions stay mutually
 * consistent: `destinationPoint(a, initialBearingDeg(a,b), distanceNm(a,b))`
 * reproduces `b`, which an ellipsoidal mix would not.
 *
 * Angles are degrees, distances nautical miles, longitude east-positive.
 */
const RAD = Math.PI / 180;
/** One nautical mile in metres (exact, by definition). */
export const METRES_PER_NM = 1852;
/** IUGG mean Earth radius R1 = (2a+b)/3 for WGS-84, in metres. */
export const EARTH_RADIUS_M = 6371008.8;
/** The same radius in nautical miles (~3440.07 NM). */
export const EARTH_RADIUS_NM = EARTH_RADIUS_M / METRES_PER_NM;
/** Normalise degrees to [0, 360). Unlike `norm360` this yields 0, not 360, for due north. */
function wrap360(deg) {
    return ((deg % 360) + 360) % 360;
}
/** Normalise longitude to [-180, 180). */
function wrapLon(deg) {
    return wrap360(deg + 180) - 180;
}
/**
 * Great-circle distance in nautical miles. Symmetric; 0 for identical points.
 * Haversine, so it stays accurate for the short legs a VFR navlog is made of.
 */
export function distanceNm(a, b) {
    const lat1 = a.lat * RAD;
    const lat2 = b.lat * RAD;
    const dLat = lat2 - lat1;
    const dLon = (b.lon - a.lon) * RAD;
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    return 2 * Math.asin(Math.min(1, Math.sqrt(h))) * EARTH_RADIUS_NM;
}
/**
 * Initial great-circle course from `a` to `b`, degrees true in [0, 360).
 *
 * This is the true course (rwK / TC) to write on the navlog; on a long leg the
 * course changes along the way, so a leg longer than ~50 NM should be split.
 * Callers rendering the LBA form should pass the value through `norm360`,
 * which writes 360 rather than 0 for due north.
 */
export function initialBearingDeg(a, b) {
    const lat1 = a.lat * RAD;
    const lat2 = b.lat * RAD;
    const dLon = (b.lon - a.lon) * RAD;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    if (y === 0 && x === 0)
        return 0;
    return wrap360(Math.atan2(y, x) / RAD);
}
/**
 * Dead reckoning: the point reached from `a` by flying `distNm` along the
 * great circle leaving on the initial true course `bearingDeg`.
 */
export function destinationPoint(a, bearingDeg, distNm) {
    const d = distNm / EARTH_RADIUS_NM;
    const brg = bearingDeg * RAD;
    const lat1 = a.lat * RAD;
    const sinLat1 = Math.sin(lat1);
    const cosLat1 = Math.cos(lat1);
    const sinD = Math.sin(d);
    const cosD = Math.cos(d);
    const sinLat2 = Math.min(1, Math.max(-1, sinLat1 * cosD + cosLat1 * sinD * Math.cos(brg)));
    const lat2 = Math.asin(sinLat2);
    const lon2 = a.lon * RAD + Math.atan2(sinD * Math.sin(brg) * cosLat1, cosD - sinLat1 * sinLat2);
    return { lat: lat2 / RAD, lon: wrapLon(lon2 / RAD) };
}
/**
 * Half-way point along the great circle from `a` to `b` — where the leg's
 * magnetic variation is taken from.
 */
export function midpoint(a, b) {
    const lat1 = a.lat * RAD;
    const lat2 = b.lat * RAD;
    const dLon = (b.lon - a.lon) * RAD;
    const bx = Math.cos(lat2) * Math.cos(dLon);
    const by = Math.cos(lat2) * Math.sin(dLon);
    const cx = Math.cos(lat1) + bx;
    const lat = Math.atan2(Math.sin(lat1) + Math.sin(lat2), Math.sqrt(cx * cx + by * by));
    const lon = a.lon * RAD + Math.atan2(by, cx);
    return { lat: lat / RAD, lon: wrapLon(lon / RAD) };
}
//# sourceMappingURL=geodesy.js.map